import { useEffect, useState } from 'react';
import classnames from 'classnames';
import { useFormik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { useConfirmInvestment } from '@/query/queries/investing/useConfirmInvestment';
import { useGetAllocateInfo } from '@/query/queries/investing/useGetAllocateInfo';
import { useCalculateBonuses } from '@/containers/EveryFiveGroupA/useCalculateBonuses';
import { BrandsForInvestmentFilledModal } from '@/modals/BrandsForInvestmentFilledModal';
import { BrandsForInvestmentStatusModal } from '@/modals/BrandsForInvestmentStatusModal';
import { NotCrowdfundingNoticeModal } from '@/modals/NotCrowdfundingNoticeModal';
import { Button } from '@scramble/ui';
import { Checkbox } from '@/webkit/form/Checkbox';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import { subtractPercent } from '@/helpers/number';
import { useChat } from '@/hooks/useChat';
import { useGetMe } from '@/hooks/useGetMe';
import { isPortugueseInvestor } from '@/helpers/user';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import AnalyticsGA from '@/analyticsGA';
import { LayoutHeader } from '../layout/Header';
import { BonusInfo } from '../BonusInfo';
import { AllocationInfo } from '../AllocationInfo';
import { BrandSelectionInfo } from '../BrandSelectionInfo';
import { useAllocationStatus } from '../../hooks/useAllocationStatus';
import { useBrandSelectionStatus } from '../../hooks/useBrandSelectionStatus';
import { VALIDATION_SCHEMA } from './schema';
import IconQuestion from '@scramble/design-system/icons/help-circle.svg';
import l from '../layout/layout.module.scss';
import s from './index.module.scss';

export const StepReview = ({ wizardId, onSuccess, onBack, setLoading }) => {
  const { t, i18n } = useTranslation();
  const { profile } = useGetMe();
  const showNotCrowdfundingNotice = isPortugueseInvestor(
    profile,
    i18n.language,
  );
  const [error, setError] = useState(null);
  const { mutateAsync: confirmInvestment } = useConfirmInvestment();
  const { refetch } = useGetAllocateInfo();
  const { openSingleModal } = useModal();
  const { allocatedGroupA, allocatedGroupB, totalAllocated, totalBonusAmount } =
    useAllocationStatus();
  const {
    selectedAndFilledBrandCount,
    fee,
    selectedBrandCount,
    excludedBrands,
    feeAmount,
  } = useBrandSelectionStatus({
    totalAllocated,
  });
  const { bonus } = useCalculateBonuses(allocatedGroupA, totalBonusAmount);
  useChat(false);

  useEffect(() => {
    selectedAndFilledBrandCount &&
      excludedBrands &&
      openSingleModal(MODAL_IDS.brands_filled_for_investment);
  }, [selectedAndFilledBrandCount, excludedBrands]);

  useEffect(() => {
    AnalyticsGA.investingWizardStep2Show({ wizard_id: wizardId });
  }, []);

  const onSubmit = async ({ has_accepted_terms }) => {
    setLoading(() => true);
    const payload = {
      has_accepted_terms_a: has_accepted_terms && !!allocatedGroupA,
      has_accepted_terms_b: has_accepted_terms && !!allocatedGroupB,
    };
    AnalyticsGA.investingWizardStep2Save({
      wizard_id: wizardId,
      commission_exist: !!excludedBrands,
    });
    const res = await confirmInvestment(payload);
    if (res?.success) {
      await refetch();
      onSuccess();
    }

    if (res?.data?.error?.code) {
      setError(res?.data?.error);
      openSingleModal(MODAL_IDS.brands_for_investment_status_modal);
    }
    setLoading(() => false);
  };

  const openReviewModal = () => {
    openSingleModal(MODAL_IDS.review_participation);
  };

  const { values, errors, touched, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      has_accepted_terms: false,
    },
    validationSchema: VALIDATION_SCHEMA(t),
    onSubmit,
  });

  const trackTermsCheck = checked => {
    const params = { wizard_id: wizardId, commission_exist: !!excludedBrands };
    checked
      ? AnalyticsGA.investingWizardStep2Check(params)
      : AnalyticsGA.investingWizardStep2Uncheck(params);
  };

  const onTermsChange = () => {
    if (!values.has_accepted_terms && showNotCrowdfundingNotice) {
      openSingleModal(MODAL_IDS.not_crowdfunding_notice);
      return;
    }
    trackTermsCheck(!values.has_accepted_terms);
    setFieldValue('has_accepted_terms', !values.has_accepted_terms);
  };

  const onNoticeAccept = () => {
    trackTermsCheck(true);
    setFieldValue('has_accepted_terms', true);
  };

  return (
    <form className={l.wrapper} onSubmit={handleSubmit}>
      <LayoutHeader
        onBack={() => {
          AnalyticsGA.investingWizardStep2Return({
            wizard_id: wizardId,
            commission_exist: !!excludedBrands,
          });
          onBack();
        }}
      />
      <div className={l.container}>
        <div className={l.content}>
          <div className={s.form}>
            <div className={s.header}>
              <h2 className={s.title}>
                {t('investing:step_review.review_participation')}
              </h2>
              <Button
                theme="neutral"
                appearance="tonal"
                size="md"
                onClick={openReviewModal}
                iconUrl={IconQuestion}
              />
            </div>
            <div className={s.info}>
              <AllocationInfo
                allocatedGroupA={subtractPercent(allocatedGroupA, fee)}
                allocatedGroupB={subtractPercent(allocatedGroupB, fee)}
                totalAllocated={totalAllocated - feeAmount}
                bonusAmount={totalBonusAmount}
              />
              {!!bonus && <BonusInfo totalBonusAmount={bonus} />}
              <BrandSelectionInfo
                totalAllocated={totalAllocated}
                step="review"
              />
            </div>
          </div>
        </div>
      </div>
      <div className={l.footer}>
        <div className={l.footer_container}>
          <div className={s.footer_content}>
            <Checkbox
              name="has_accepted_terms"
              checked={values.has_accepted_terms}
              onChange={onTermsChange}
            >
              <span
                className={classnames(
                  s.checkbox,
                  touched.has_accepted_terms &&
                    errors.has_accepted_terms &&
                    s.checkbox_error,
                )}
              >
                {allocatedGroupB ? (
                  <Trans
                    ns="investing"
                    i18nKey="step_review.acknowledge_terms_moderate"
                    components={{
                      LinkTermsOfUse: (
                        <MyLink
                          href={APP_LINKS.termOfUse}
                          isExternal
                          underline
                        />
                      ),
                      LinkGuaranteeLetter: (
                        <MyLink
                          href={APP_LINKS.subordinatedGuaranteeLetter}
                          isExternal
                          underline
                        />
                      ),
                      LinkAssignmentAgreement: (
                        <MyLink
                          href={APP_LINKS.docAssignmentAgreement}
                          isExternal
                          underline
                        />
                      ),
                    }}
                  />
                ) : (
                  <Trans
                    ns="investing"
                    i18nKey="step_review.acknowledge_terms"
                    components={{
                      LinkTermsOfUse: (
                        <MyLink
                          href={APP_LINKS.termOfUse}
                          isExternal
                          underline
                        />
                      ),
                      LinkGuaranteeLetter: (
                        <MyLink
                          href={APP_LINKS.subordinatedGuaranteeLetter}
                          isExternal
                          underline
                        />
                      ),
                      LinkAssignmentAgreement: (
                        <MyLink
                          href={APP_LINKS.docAssignmentAgreement}
                          isExternal
                          underline
                        />
                      ),
                    }}
                  />
                )}
              </span>
            </Checkbox>
            <Button
              theme="brand"
              appearance="filled"
              size="lg"
              type="submit"
              className={s.btn_submit}
            >
              {t('investing:step_review.save')}
            </Button>
          </div>
        </div>
      </div>

      <>
        <BrandsForInvestmentStatusModal error={error} onAddBrands={onBack} />
        <NotCrowdfundingNoticeModal onAccept={onNoticeAccept} />
        <BrandsForInvestmentFilledModal
          wizardId={wizardId}
          remainingBrands={selectedBrandCount - selectedAndFilledBrandCount}
          allocation={{
            total: totalAllocated - feeAmount,
            conservative: subtractPercent(allocatedGroupA, fee),
            moderate: subtractPercent(allocatedGroupB, fee),
          }}
          onContinue={handleSubmit}
          onBrandsClick={() => {
            onBack();
            setTimeout(openSingleModal(MODAL_IDS.brands_for_investment), 1000);
          }}
        />
      </>
    </form>
  );
};
